import React, { useState } from 'react';
import { MapPin, Clock, Phone, AlertTriangle, CheckCircle, Building } from 'lucide-react';

const CountyProcessGuide = ({ language = 'en' }) => {
  const [selectedCounty, setSelectedCounty] = useState('sacramento');

  const countyData = {
    sacramento: {
      name: language === 'es' ? 'Condado de Sacramento' : 'Sacramento County',
      facilities: [
        {
          name: language === 'es' ? 'Cárcel Principal del Condado de Sacramento' : 'Sacramento County Main Jail',
          address: '651 I Street, Sacramento, CA 95814',
          phone: '(916) 874-6750',
          processingTime: language === 'es' ? '2-3 horas' : '2-3 hours',
          releaseSchedule: language === 'es' ? '24/7 - Liberación continua' : '24/7 - Continuous release'
        },
        {
          name: language === 'es' ? 'Centro Correccional Rio Cosumnes' : 'Rio Cosumnes Correctional Center', 
          address: '12500 Bruceville Road, Elk Grove, CA 95757',
          phone: '(916) 874-7531',
          processingTime: language === 'es' ? '3-4 horas' : '3-4 hours',
          releaseSchedule: language === 'es' ? '6AM, 2PM, 10PM diariamente' : '6AM, 2PM, 10PM daily'
        }
      ],
      specialNotes: language === 'es' ? [
        'Sacramento procesa liberaciones más rápido los días de semana',
        'Los fines de semana pueden tomar 1-2 horas adicionales',
        'La cárcel principal opera liberación 24/7'
      ] : [
        'Sacramento processes releases faster on weekdays',
        'Weekends may take an additional 1-2 hours', 
        'Main jail operates 24/7 release'
      ]
    },
    sanjoaquin: {
      name: language === 'es' ? 'Condado de San Joaquín' : 'San Joaquin County',
      facilities: [
        {
          name: language === 'es' ? 'Cárcel del Condado de San Joaquín' : 'San Joaquin County Jail',
          address: '7000 Michael Canlis Blvd, French Camp, CA 95231',
          phone: '(209) 468-4400',
          processingTime: language === 'es' ? '3-5 horas' : '3-5 hours',
          releaseSchedule: language === 'es' ? '8AM, 4PM, 12AM diariamente' : '8AM, 4PM, 12AM daily'
        }
      ],
      specialNotes: language === 'es' ? [
        'Tiempo de procesamiento más largo debido a la distancia',
        'Recomendamos programar recogida después de las 8AM',
        'Los viernes por la noche pueden demorar hasta 6 horas'
      ] : [
        'Longer processing time due to distance',
        'We recommend scheduling pickup after 8AM',
        'Friday nights may take up to 6 hours'
      ]
    },
    stanislaus: {
      name: language === 'es' ? 'Condado de Stanislaus' : 'Stanislaus County',
      facilities: [
        {
          name: language === 'es' ? 'Centro de Seguridad Pública de Stanislaus' : 'Stanislaus County Public Safety Center',
          address: '800 11th Street, Modesto, CA 95354',
          phone: '(209) 525-7114',
          processingTime: language === 'es' ? '3-4 horas' : '3-4 hours',
          releaseSchedule: language === 'es' ? '6AM-10PM (limitado de noche)' : '6AM-10PM (limited overnight)'
        }
      ],
      specialNotes: language === 'es' ? [
        'Liberación nocturna limitada - puede demorar hasta la mañana',
        'Instalación más nueva con procesamiento eficiente',
        'Mejor llamarnos antes de las 8PM para liberación el mismo día'
      ] : [
        'Limited overnight release - may wait until morning',
        'Newer facility with efficient processing',
        'Best to call us before 8PM for same-day release'
      ]
    }
  };

  const text = {
    en: {
      title: 'County-Specific Process Guide',
      subtitle: 'Each county has different procedures and timing - know what to expect',
      selectCounty: 'Select Your County',
      facilities: 'Jail Facilities',
      processingTime: 'Typical Processing Time',
      releaseSchedule: 'Release Schedule',
      specialNotes: 'Important Notes',
      address: 'Address',
      phone: 'Phone',
      callForUpdate: 'Call for Real-Time Updates',
      travelNote: 'We serve all these counties with experienced agents familiar with each facility\'s specific procedures.'
    },
    es: {
      title: 'Guía de Proceso Específico por Condado', 
      subtitle: 'Cada condado tiene diferentes procedimientos y tiempos - sepa qué esperar',
      selectCounty: 'Seleccione Su Condado',
      facilities: 'Instalaciones Carcelarias',
      processingTime: 'Tiempo de Procesamiento Típico',
      releaseSchedule: 'Horario de Liberación',
      specialNotes: 'Notas Importantes',
      address: 'Dirección',
      phone: 'Teléfono',
      callForUpdate: 'Llamar para Actualizaciones en Tiempo Real',
      travelNote: 'Servimos todos estos condados con agentes experimentados familiarizados con los procedimientos específicos de cada instalación.'
    }
  };

  const currentText = text?.[language] || text?.en;
  const counties = Object.keys(countyData);

  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6 flex items-center justify-center">
          <MapPin className="w-10 h-10 mr-4 text-blue-600" aria-hidden="true" />
          {currentText?.title}
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
          {currentText?.subtitle}
        </p>
      </div>
      {/* County Selector */}
      <div className="flex justify-center mb-12">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {counties?.map((countyKey) => (
              <button
                key={countyKey}
                onClick={() => setSelectedCounty(countyKey)}
                className={`px-6 py-3 rounded-xl font-semibold text-center transition-all ${
                  selectedCounty === countyKey
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {countyData?.[countyKey]?.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* County Details */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8">
          <h3 className="text-3xl font-bold mb-2">
            {countyData?.[selectedCounty]?.name}
          </h3>
          <p className="text-blue-100">
            {currentText?.travelNote}
          </p>
        </div>

        <div className="p-8">
          {/* Facilities Section */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Building className="w-8 h-8 mr-3 text-blue-600" aria-hidden="true" />
              {currentText?.facilities}
            </h4>
            
            <div className="space-y-6">
              {countyData?.[selectedCounty]?.facilities?.map((facility, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <div className="lg:flex lg:items-start lg:space-x-8">
                    <div className="lg:w-2/3 mb-6 lg:mb-0">
                      <h5 className="text-xl font-bold text-gray-900 mb-4">
                        {facility?.name}
                      </h5>
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-start">
                          <MapPin className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <span className="font-semibold text-gray-600">{currentText?.address}:</span>
                            <div className="text-gray-700">{facility?.address}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <Phone className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0 mt-0.5" aria-hidden="true" />
                          <div>
                            <span className="font-semibold text-gray-600">{currentText?.phone}:</span>
                            <div className="text-gray-700">
                              <a href={`tel:${facility?.phone}`} className="hover:text-blue-600 transition-colors">
                                {facility?.phone}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="lg:w-1/3">
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <div className="text-center mb-4">
                          <Clock className="w-8 h-8 text-blue-600 mx-auto mb-2" aria-hidden="true" />
                          <h6 className="font-bold text-gray-900">{currentText?.processingTime}</h6>
                          <div className="text-2xl font-black text-blue-600">{facility?.processingTime}</div>
                        </div>
                        
                        <div className="text-center">
                          <h6 className="font-semibold text-gray-600 mb-1">{currentText?.releaseSchedule}</h6>
                          <div className="text-sm text-gray-700">{facility?.releaseSchedule}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Special Notes */}
          <div className="mb-8">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3 text-amber-600" aria-hidden="true" />
              {currentText?.specialNotes}
            </h4>
            
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <ul className="space-y-3">
                {countyData?.[selectedCounty]?.specialNotes?.map((note, index) => (
                  <li key={index} className="flex items-start text-amber-800">
                    <CheckCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-lg leading-relaxed">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-2xl text-center">
            <h4 className="text-2xl font-bold mb-4">
              {currentText?.callForUpdate}
            </h4>
            <p className="text-xl text-blue-100 mb-6">
              {language === 'es' ?'Nuestros agentes conocen los horarios actuales y pueden darle estimaciones precisas.' :'Our agents know current schedules and can give you precise estimates.'
              }
            </p>
            <a 
              href="tel:+19168330052"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all inline-flex items-center"
            >
              <Phone className="w-6 h-6 mr-3" aria-hidden="true" />
              {language === 'es' ? 'Llamar Agente Local' : 'Call Local Agent'}: (916) 833-0052
            </a>
          </div>
        </div>
      </div>
      {/* Coverage Map */}
      <div className="mt-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200">
        <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {language === 'es' ? 'Nuestra Cobertura de Servicio' : 'Our Service Coverage'}
        </h4>
        
        <div className="grid md:grid-cols-3 gap-6 text-center">
          {counties?.map((countyKey) => {
            const county = countyData?.[countyKey];
            return (
              <div key={countyKey} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                <MapPin className="w-12 h-12 text-blue-600 mx-auto mb-4" aria-hidden="true" />
                <h5 className="text-xl font-bold text-gray-900 mb-2">{county?.name}</h5>
                <div className="text-sm text-gray-600 mb-4">
                  {county?.facilities?.length} {language === 'es' ? 'instalación(es)' : 'facility(ies)'}
                </div>
                <div className="text-lg font-semibold text-blue-600">
                  {county?.facilities?.[0]?.processingTime}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {language === 'es' ? 'tiempo promedio' : 'average time'}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-8">
          <p className="text-gray-600 text-lg">
            {language === 'es' ?'Servimos todos los condados del Valle Central con agentes locales experimentados' :'We serve all Central Valley counties with experienced local agents'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default CountyProcessGuide;